/**
 * MobileCheckoutSheet — full-screen mobile checkout takeover.
 *
 * Rebuilt (2026-07-01) as a TRADITIONAL single-page checkout, modeled on the
 * Maven.com mobile enroll flow: uncluttered white cards on cream, one scroll,
 * everything in the order a buyer expects —
 *   1. ORDER      — workshop title + format/duration line
 *   2. DATE       — native-radio date cards (weekday, date, time, seats-left)
 *   3. SEATS      — one stepper; the tier is derived from the count
 *                   (1–2 = individual, 3+ = team rate, 6+ = volume) + VIP addon
 *   4. SUMMARY    — line items, promo code (collapsed "+ Add promo code"),
 *                   early-bird/team/coupon savings, bold Total
 *   5. GUARANTEE  — money-back reassurance
 *   6. TESTIMONIAL— one real published review (swap point marked below)
 *   7. PAYMENT    — Stripe Embedded Checkout inline (card, wallets, email,
 *                   3DS all inside), with a hosted-redirect fallback when no
 *                   publishable key is configured.
 *
 * The checkout POST body is IDENTICAL to the desktop rail:
 *   { ticketTypeId, quantity, eventSlug, couponCode, premiumAddon, email,
 *     fbp, fbc, ...utm } → both /api/checkout and /api/checkout/embedded
 * share one server-side session builder, so Stripe triggers, the webhook →
 * orders sync, Clara's email, and audience routing are untouched.
 *
 * Accessibility: real <input type="radio"> under every selectable card
 * (native keyboard semantics), focus trap + focus restore, aria-live on
 * async coupon/error updates, prefers-reduced-motion respected.
 *
 * UNIVERSAL (2026-07-02): below `sm` this is the full-screen mobile takeover;
 * from `sm` up it renders as a right-anchored checkout DRAWER (Shopify-style
 * shelf, 480px, dim backdrop, slide-in from the right). One component, one
 * checkout contract, every screen size.
 */
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Check, Shield, Lock, Minus, Plus, Star, Users, Clock, ChevronDown, ThumbsUp, Laptop } from "lucide-react";
// The /pure entry point matters: the default "@stripe/stripe-js" module
// injects Stripe.js onto the page as an IMPORT side effect (Stripe does this
// on purpose, for fraud-signal coverage) — which defeats any lazy loadStripe
// call. /pure only injects when loadStripe is actually invoked.
import { loadStripe } from "@stripe/stripe-js/pure";
import type { Stripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import type { EventWithSeries, SeriesSessionSummary } from "@shared/schema";
import { CLIENT_QUOTE, checkoutReviewFor } from "@shared/workshop-reviews";
import { formatPrice } from "@/components/sections/workshop-ticket-block";
import type { CouponStatus } from "@/components/sections/workshop-ticket-block";
import { useBodyScrollLock } from "@/lib/body-scroll-lock";
import { KIDS_MAX_CHILDREN, KIDS_SIBLING_PERCENTS, KIDS_COHORT_SCHEDULE, kidsChildPriceCents } from "@shared/kids-camp";
import { KidsProgramTermsBody } from "@/components/kids/program-terms-content";

/** Volume pricing — MUST stay in sync with server/routes.ts and
 *  workshop-ticket-block.tsx (all three intentionally duplicate this pair). */
const VOLUME_THRESHOLD = 6;
const VOLUME_PRICE_CENTS = 49900;

/** Compact one-line date for <option> text: "Fri, Jul 3 · 10:00 AM – 1:30 PM EDT" */
function sessionDateShort(session: SeriesSessionSummary) {
  const tz = session.timezone;
  const start = new Date(session.startDatetime as unknown as string);
  const end = new Date(session.endDatetime as unknown as string);
  const day = start.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: tz });
  const t1 = start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz });
  const t2 = end.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz, timeZoneName: "short" });
  return `${day} · ${t1} – ${t2}`;
}

/* The T-Mobile quote now lives in shared/workshop-reviews.ts — the team page
   leads its testimonials with it, and two hand-kept copies of a real client's
   words is exactly the kind of thing that drifts. */
const CHECKOUT_TESTIMONIAL = CLIENT_QUOTE;

/** Kids-camp reviews — REAL, supplied verbatim by Isaac (2026-07-21):
 *  one parent voice + one camper voice. These replace the adult-workshop
 *  quotes in kids mode (FTC 465: never imply adult reviews are camp reviews —
 *  these ARE camp reviews, so no cross-labeling needed). */
const KIDS_CHECKOUT_TESTIMONIALS = [
  {
    text: "My sons learned Claude Code with AdVenture Media and they are each continuing to build their own apps ... even during TV watching time.",
    name: "Brett LaBrie",
    source: "Camp parent",
  },
  {
    text: "I built a vocabulary game during camp and I actually got a 100 on my next vocabulary test. My game gives me 5 minutes of MLB highlights when I get 10 answers right in a row.",
    name: "Aaron R.",
    source: "Camper",
  },
];

/** Trust strip — 2 rows × 3, same grayscale logo files the page hero uses. */
const CHECKOUT_LOGOS = [
  { src: "/images/client-logos/forbes-2.webp", alt: "Forbes" },
  { src: "/images/client-logos/nasdaq-480.webp", alt: "Nasdaq" },
  { src: "/images/client-logos/tmobile-480.webp", alt: "T-Mobile" },
  { src: "/images/client-logos/amc-networks-480.webp", alt: "AMC Networks" },
  { src: "/images/client-logos/pbs.svg", alt: "PBS" },
  { src: "/images/client-logos/kars4kids.webp", alt: "Kars4Kids" },
];

/** One purchasable ticket tier for a session, pre-resolved by the parent. */
export interface TicketOption {
  id: number;
  kind: "individual" | "team";
  /** Effective (early-bird-aware) per-seat price in cents. */
  priceCents: number;
  /** List price in cents — struck through when below it. */
  fullPriceCents: number;
  earlyBird: boolean;
  minQty: number;
  maxQty: number;
}

/**
 * One bookable session, pre-resolved by the parent (which owns all pricing /
 * early-bird / series-slug logic) so this component never re-derives money.
 */
export interface BookableSession {
  /** Session id (the primary event id for a single-date workshop). */
  id: number;
  session: SeriesSessionSummary;
  /** DB slug for the checkout API (NOT the URL/series slug). */
  effectiveSlug: string;
  /** Remaining seats, when known and worth surfacing. */
  seatsRemaining: number | null;
  /** Purchasable tiers — individual always present, team when the session has one. */
  tickets: TicketOption[];
}

export interface PremiumAddonInfo {
  label: string;
  description: string;
  priceCents: number;
  allowOnTeam: boolean;
}

/**
 * Publishable key for the in-page embed. Vite inlines `VITE_`-prefixed env
 * vars at build time; the hardcoded fallback covers production, where Cloud
 * Build passes no VITE_ vars into the client build. Publishable keys are
 * public by design (browser-side, tokenize-only) — safe to commit. The
 * SECRET key never goes near this file.
 */
const STRIPE_PUBLISHABLE_KEY: string | undefined =
  (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined) ??
  "pk_live_e6LOxL541u7AQ3lWQvEg9fQu";

/* Perf: Stripe.js is ~820KB of third-party JS across four files, and this
   sheet mounts (closed) on every workshop page view — calling loadStripe
   during the initial render made every visitor download all of it before
   showing any purchase intent (~47% of the page's mobile byte weight).
   Defer-then-prewarm instead: nothing at initial load, then a quiet warm on
   a 6s post-mount timer (well past LCP/onload), so the first checkout open
   still finds Stripe already downloaded. Opening the sheet before the timer
   fires starts the load immediately — same as the old first-open path. */
let stripePromise: Promise<Stripe | null> | null = null;
function getStripePromise() {
  if (!STRIPE_PUBLISHABLE_KEY) return null;
  if (!stripePromise) stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  return stripePromise;
}

export interface MobileCheckoutSheetProps {
  open: boolean;
  onClose: () => void;
  event: EventWithSeries;
  /** Upcoming bookable sessions, soonest first. Never empty when rendered. */
  sessions: BookableSession[];
  /** Which session to preselect (the buyer's pick on the page). */
  initialSessionId?: number | null;
  /** Series-keyed VIP addon config; null hides the addon entirely. */
  premiumAddon?: PremiumAddonInfo | null;
  prefillEmail?: string;
  utmParams?: Record<string, string | undefined>;
  /** URL-captured promo code (?coupon=…) passed through by the parent —
   *  auto-applied ONCE on the first closed→open transition, unless the buyer
   *  has already typed/applied a code this session. */
  initialCouponCode?: string;
  /** Claude Code for Kids family mode — per-camper rows (first name + age),
   *  the flat sibling ladder, consent gates, and parent-appropriate trust
   *  content. Set by WorkshopTicketBlock for the kids series ONLY. Promo
   *  codes and VIP addons don't exist in this mode. */
  kids?: { termsHref: string } | null;
  /** `?vip=1` deep link (recap emails): on open, go straight to the
   *  VIP-standalone checkout, exactly as if the buyer had clicked
   *  "Purchase VIP standalone". Ignored when VIP isn't offered. */
  autoVipOnly?: boolean;
}

/** "Left to enroll" countdown, anchored to the server's T−1h checkout cutoff.
 *  Coarse on purpose (days/hrs/min) — no ticking timer needed. */
function enrollWindowLeft(start: Date): string | null {
  const ms = start.getTime() - Date.now() - 60 * 60 * 1000;
  if (ms <= 0) return null;
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  if (days > 0) return `${days} ${days === 1 ? "day" : "days"} ${hours} ${hours === 1 ? "hr" : "hrs"}`;
  if (hours > 0) return `${hours} ${hours === 1 ? "hr" : "hrs"} ${mins} min`;
  return `${mins} min`;
}

/* ── Shared date formatting (Intl-backed, always the session's own tz) ── */
function sessionDateParts(session: SeriesSessionSummary) {
  const tz = session.timezone;
  const start = new Date(session.startDatetime as unknown as string);
  const end = new Date(session.endDatetime as unknown as string);
  return {
    weekday: start.toLocaleDateString("en-US", { weekday: "long", timeZone: tz }),
    monthDay: start.toLocaleDateString("en-US", { month: "long", day: "numeric", timeZone: tz }),
    timeRange: `${start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz })} – ${end.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz, timeZoneName: "short" })}`,
    durationHours: Math.round(((end.getTime() - start.getTime()) / 36e5) * 2) / 2,
  };
}

export function MobileCheckoutSheet({
  open,
  onClose,
  event,
  sessions,
  initialSessionId,
  premiumAddon = null,
  prefillEmail,
  utmParams = {},
  initialCouponCode,
  kids = null,
  autoVipOnly = false,
}: MobileCheckoutSheetProps) {
  const [selectedId, setSelectedId] = useState<number | null>(initialSessionId ?? null);
  const [qtyState, setQty] = useState(1);
  const [vip, setVip] = useState(false);

  // ── Claude Code for Kids family mode ──
  // Camper rows ARE the quantity (age kept as a string for the select,
  // validated on the way out). Camper 1's card is OPEN by default (Isaac
  // 2026-07-21 v2); the "Enroll your child +" button only reappears if the
  // parent removes every row. Payment stays locked until at least one camper
  // row is complete and the Program Terms are accepted — which also
  // guarantees the Stripe session's metadata.children is final when the
  // embed mounts.
  const kidsMode = !!kids;
  /* The checkout review follows the workshop being bought — a Cowork review
     shown on a Code purchase reads as proof for the wrong product. */
  const checkoutReview = checkoutReviewFor(event.seriesSlug);
  const [campers, setCampers] = useState<Array<{ firstName: string; lastName: string; age: string }>>([
    { firstName: "", lastName: "", age: "" },
  ]);
  // Parent/guardian name — captured explicitly so the order + emails use the
  // registering parent, not Stripe's cardholder/Link name.
  const [parentName, setParentName] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [mediaOptIn, setMediaOptIn] = useState(false);
  // Collapsible cohort schedule card + the scrollable Program Terms modal.
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  // The sheet's Escape handler (window-level, [open]-scoped) reads this ref
  // so Escape closes the terms modal FIRST instead of the whole checkout —
  // portal DOM sits outside the React root, so stopPropagation inside the
  // modal can't reliably shield the sheet's native window listener.
  const termsModalOpenRef = useRef(termsModalOpen);
  useEffect(() => {
    termsModalOpenRef.current = termsModalOpen;
  });
  // Kids mode with no camper yet still prices/labels off a 1-camper order
  // (qty 0 would zero the embed key and hide the ladder); payment stays
  // locked until a real camper row exists, so nothing can be bought at it.
  const qty = kidsMode ? Math.max(campers.length, 1) : qtyState;
  const [couponCode, setCouponCode] = useState("");
  const [couponStatus, setCouponStatus] = useState<CouponStatus | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [vipOnlyLoading, setVipOnlyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sheetRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Latest-onClose ref. The parent passes onClose as an inline arrow and
  // re-renders on seat-clock ticks, so onClose is a NEW function every tick —
  // any effect listing it as a dependency re-arms on every parent render.
  // Effects read this ref instead and depend on [open] only. (The focus-trap
  // effect was the bite: its re-arm cleanup restored focus to the opener and
  // setup re-focused the sheet root, yanking the buyer out of the Stripe
  // iframe and dismissing the iOS keyboard mid-payment.)
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // The embed-vs-hosted rail is decided by key presence alone; the actual
  // Stripe.js load starts at prewarm (timer below) or first open, whichever
  // comes first. When closed this render value is unused (sheet returns null).
  const useEmbedded = !!STRIPE_PUBLISHABLE_KEY;
  const stripePromiseForRender = open ? getStripePromise() : stripePromise;

  // Prewarm: start the Stripe.js download once the initial load has settled.
  useEffect(() => {
    if (!STRIPE_PUBLISHABLE_KEY) return;
    const t = window.setTimeout(() => getStripePromise(), 6000);
    return () => window.clearTimeout(t);
  }, []);

  // Validate a promo code against the server. Takes the code explicitly (not
  // from state) so the URL-coupon adoption below can validate before the
  // setCouponCode render flushes; the Apply button delegates here too.
  // seriesSlug rides along so scoped codes (kids-only 5TJT/JEWISHLINK, and
  // any future per-series code) validate with the SAME scope rule the
  // checkout applies — otherwise the sheet would show "valid" for a code
  // the server is about to ignore.
  const eventSeriesSlug = event.seriesSlug;
  const validateCoupon = useCallback(
    async (code: string) => {
      if (!code.trim()) return;
      setCouponLoading(true);
      try {
        const res = await fetch("/api/validate-coupon", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, seriesSlug: eventSeriesSlug }),
        });
        setCouponStatus(await res.json());
      } catch {
        setCouponStatus({ valid: false, message: "Couldn't validate the code. Try again." });
      } finally {
        setCouponLoading(false);
      }
    },
    [eventSeriesSlug],
  );

  // Adopt the page's pick on every open: `selectedId` persists across
  // open/close, so a still-valid stale selection would otherwise ignore a
  // newly clicked date card (hero date cards select the session, then fire
  // the open event). Only the closed→open transition adopts — while the
  // sheet is open the buyer's in-sheet choice always wins.
  const prevOpenRef = useRef(open);
  // URL coupon is adopted at most ONCE per mount: if the buyer removes it
  // deliberately (X on the summary line), a later open must NOT re-apply it.
  const appliedInitialCouponRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!open || wasOpen) return;
    if (initialSessionId != null && sessions.some((s) => s.id === initialSessionId)) {
      setSelectedId(initialSessionId);
    }
    // URL-captured coupon (?coupon=…): apply + auto-validate on first open,
    // but never clobber a code the buyer already typed or applied. On an
    // invalid code this lands in the normal invalid-coupon state with the
    // code visible in the expanded promo input, so the buyer sees what
    // happened and can correct it.
    // Kids mode adopts URL coupons too (2026-07-21 v2): kids-scoped codes
    // (5TJT/JEWISHLINK reader offers) arrive via ?coupon= campaign links.
    // A stale global code (USA50 in localStorage) simply validates invalid
    // for this series — visible, not silently ignored.
    const urlCode = initialCouponCode?.trim();
    if (urlCode && !appliedInitialCouponRef.current && !couponCode.trim() && !couponStatus) {
      appliedInitialCouponRef.current = true;
      setCouponCode(urlCode);
      setCouponOpen(true);
      void validateCoupon(urlCode);
    }
  }, [open, initialSessionId, sessions, initialCouponCode, couponCode, couponStatus, validateCoupon]);

  // Keep the selection valid: default to the preselected id, else the first
  // session; re-sync when the sheet re-opens or the session list changes.
  useEffect(() => {
    if (!open) return;
    const valid = sessions.some((s) => s.id === selectedId);
    if (!valid) setSelectedId(initialSessionId ?? sessions[0]?.id ?? null);
  }, [open, sessions, initialSessionId, selectedId]);

  const selected = useMemo(
    () => sessions.find((s) => s.id === selectedId) ?? sessions[0] ?? null,
    [sessions, selectedId],
  );

  const indivTicket = selected?.tickets.find((t) => t.kind === "individual") ?? null;
  const teamTicket = selected?.tickets.find((t) => t.kind === "team") ?? null;

  // The tier is DERIVED from the seat count — buyers think "how many people,"
  // not "which SKU." 1–2 seats buy the individual ticket; hitting the team
  // minimum (3) switches to the team tier ONLY when the team effective
  // per-seat price (including the 6+ volume floor when it applies) is
  // STRICTLY cheaper than the individual EFFECTIVE (early-bird-aware) price —
  // the auto-switch must never reprice a buyer UPWARD. When team isn't
  // cheaper, there's no switch, no team hint, no savings line: individual
  // pricing simply continues at any quantity. The server receives the
  // resolved ticketTypeId + quantity, identical to what the desktop's
  // explicit toggle would send.
  const teamEffectivePerSeat = teamTicket
    ? qty >= VOLUME_THRESHOLD
      ? Math.min(VOLUME_PRICE_CENTS, teamTicket.priceCents)
      : teamTicket.priceCents
    : Infinity;
  const teamActive =
    !kidsMode &&
    !!teamTicket &&
    qty >= teamTicket.minQty &&
    (!indivTicket || teamEffectivePerSeat < indivTicket.priceCents);
  // Kids mode hard-pins the individual ticket: the family ladder is
  // displayed off indivTicket, so the id POSTed must be the same ticket —
  // a team auto-switch here would diverge display from charge.
  const activeTicket = kidsMode ? indivTicket : teamActive ? teamTicket! : indivTicket;
  const ticketKind: "individual" | "team" = teamActive ? "team" : "individual";
  const maxSeats = teamTicket ? teamTicket.maxQty : indivTicket?.maxQty ?? 1;

  // Clamp the seat count when the session (and its tiers) changes.
  useEffect(() => {
    setQty((q) => Math.min(Math.max(q, 1), maxSeats));
  }, [maxSeats]);

  // Lock the page behind the takeover via the SHARED ref-counted lock
  // (client/src/lib/body-scroll-lock.ts). Never hand-roll body style saves
  // here: overlapping modals restoring each other's snapshots froze the page
  // (2026-07-02 preview bug). position:fixed under the hood for iOS Safari.
  useBodyScrollLock(open);

  // Reset transient state on close.
  useEffect(() => {
    if (!open) {
      setError(null);
      setCouponOpen(false);
    }
  }, [open]);

  // (The old ≥640px auto-close is gone: the sheet now renders at every
  // breakpoint — full-screen below `sm`, right drawer above — so crossing
  // the breakpoint just restyles it instead of stranding a locked body.)

  // Tick while open so the "Left to enroll" line (computed per render from
  // Date.now()) stays current during an idle sheet — and disappears via its
  // existing null check once past the T−1h cutoff. Coarse 30s cadence.
  const [, setCountdownTick] = useState(0);
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setCountdownTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, [open]);

  // Focus management: remember the opener, move focus into the dialog, trap
  // Tab inside it, close on Escape, and restore focus on close.
  // Depends on [open] ONLY — Escape reads onCloseRef so the parent's inline
  // onClose (recreated every seat-clock tick) can never re-arm this trap
  // mid-payment. Re-arming yanked focus out of the Stripe iframe on every
  // parent re-render (cleanup focused the opener, setup re-focused the sheet
  // root), dismissing the iOS keyboard while the buyer typed their card.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = (document.activeElement as HTMLElement) ?? null;
    sheetRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Terms modal open? Escape dismisses it, not the whole checkout.
        if (termsModalOpenRef.current) {
          setTermsModalOpen(false);
          return;
        }
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !sheetRef.current) return;
      const focusables = sheetRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, iframe, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      restoreFocusRef.current?.focus?.();
    };
  }, [open]);

  // Scroll-guard: the root must never scroll — the .mcs-body owns the single
  // scroll. `overflow: clip` (above) prevents this on modern browsers, but on
  // any engine without clip support (or during a mid-resize transient) a
  // focus-driven scrollIntoView can still scroll the root, stranding the
  // header + close button off-screen. Pin it back to 0 on any scroll. (Where
  // clip is honored no scroll event ever fires, so this is a no-op there.)
  useEffect(() => {
    if (!open) return;
    const el = sheetRef.current;
    if (!el) return;
    const pin = () => {
      if (el.scrollTop !== 0) el.scrollTop = 0;
      if (el.scrollLeft !== 0) el.scrollLeft = 0;
    };
    el.addEventListener("scroll", pin, { passive: true });
    return () => el.removeEventListener("scroll", pin);
  }, [open]);

  /* ── Pricing (display only — the server recomputes authoritatively) ── */
  const vipAllowed = !kidsMode && !!premiumAddon && (ticketKind === "individual" || premiumAddon.allowOnTeam);
  const effectiveVip = vip && vipAllowed;
  const volumeActive = teamActive && qty >= VOLUME_THRESHOLD && VOLUME_PRICE_CENTS < (teamTicket?.priceCents ?? Infinity);
  const basePerSeat = activeTicket
    ? teamActive && qty >= VOLUME_THRESHOLD
      ? Math.min(VOLUME_PRICE_CENTS, activeTicket.priceCents)
      : activeTicket.priceCents
    : 0;
  const perSeatWithVip = basePerSeat + (effectiveVip && premiumAddon ? premiumAddon.priceCents : 0);

  /* ── Kids family-mode pricing — display mirror of shared/kids-camp.ts;
        the server recomputes authoritatively from the same module. ── */
  const kidsBaseCents = indivTicket?.priceCents ?? 0;
  const kidsListBaseCents = indivTicket?.fullPriceCents ?? kidsBaseCents;
  const kidsLadder = kidsMode ? campers.map((_, i) => kidsChildPriceCents(kidsBaseCents, i + 1)) : [];
  const kidsSubtotal = kidsLadder.reduce((a, b) => a + b, 0);
  // Summary anchoring (Isaac 2026-07-21 v3): every camper line shows the
  // FULL list price, then two green lines subtract down to the real
  // subtotal — so the visible column resolves exactly:
  //   listBase × n  −  Family savings  −  Early bird discount  =  subtotal
  //   (2 kids: $2,798 − $490 − $660 = $1,648 ✓)
  // "Family savings" = the sibling ladder at LIST prices; "Early bird
  // discount" = list ladder − effective ladder. Never label anything
  // "founding" — the cohort's number is not public.
  const kidsListLadder = kidsMode ? campers.map((_, i) => kidsChildPriceCents(kidsListBaseCents, i + 1)) : [];
  const kidsListLadderTotal = kidsListLadder.reduce((a, b) => a + b, 0);
  const kidsFamilySavings = kidsMode ? Math.max(0, kidsListBaseCents * campers.length - kidsListLadderTotal) : 0;
  const kidsEarlyBirdSavings = kidsMode ? Math.max(0, kidsListLadderTotal - kidsSubtotal) : 0;
  const campersComplete =
    kidsMode &&
    campers.length > 0 &&
    campers.every(
      (c) =>
        c.firstName.trim().length > 0 &&
        c.lastName.trim().length > 0 &&
        /^\d{1,2}$/.test(c.age.trim()) &&
        +c.age >= 10 &&
        +c.age <= 15,
    );
  const parentNameValid = parentName.trim().length >= 2;
  const kidsPaymentUnlocked = !kidsMode || (campersComplete && parentNameValid && termsAccepted);

  const subtotal = kidsMode ? kidsSubtotal : perSeatWithVip * qty;
  // Savings anchor: team pricing is anchored against the individual
  // EFFECTIVE per-seat rate — indivTicket.priceCents is already
  // early-bird-aware from the parent resolver (see TicketOption), NEVER the
  // fullPriceCents list price — so "Team discount −$X" is the real delta the
  // buyer would otherwise pay for these seats. teamActive already guarantees
  // basePerSeat < indivTicket.priceCents, so Math.max is only a floor guard.
  // Solo buyers anchor against the list price when an early bird is live.
  const perSeatAnchor = teamActive
    ? Math.max(indivTicket?.priceCents ?? basePerSeat, basePerSeat)
    : activeTicket?.fullPriceCents ?? basePerSeat;
  const savings = (perSeatAnchor - basePerSeat) * qty;

  const couponDiscount = useMemo(() => {
    if (!couponStatus?.valid) return 0;
    // Fixed-price codes set the whole cart to fixedPriceCents. In kids mode
    // `subtotal` is the ladder subtotal, which the server also anchors to, so
    // the displayed discount matches the charge.
    if (couponStatus.fixedPriceCents) return Math.max(0, subtotal - couponStatus.fixedPriceCents);
    if (couponStatus.percentOff) return Math.round((subtotal * couponStatus.percentOff) / 100);
    return 0;
  }, [subtotal, couponStatus, kidsMode]);
  const total = subtotal - couponDiscount;

  // Remount the embed (fresh clientSecret) whenever anything priced changes —
  // the provider fetches the secret once per mount. DEBOUNCED: every stepper
  // tap would otherwise remount instantly (new Stripe session per tap, wipes
  // typed card/email, 1–2s blank), so the provider key follows embedKey only
  // after ~700ms of quiet. fetchClientSecret reads current state via closure
  // and the provider calls it once on mount, so the fresh mount gets fresh
  // pricing regardless of which key value triggered it.
  // Kids mode extends the key with the camper rows + media opt-in: those
  // land in the session's metadata, so an edit after the embed mounted must
  // remount it (debounced below) to keep the Stripe session truthful.
  const embedKey = `${activeTicket?.id ?? "x"}:${qty}:${effectiveVip}:${couponStatus?.valid ? couponCode : ""}:${selected?.effectiveSlug ?? "x"}${kidsMode ? `:k:${JSON.stringify(campers)}:${mediaOptIn}:${parentName.trim()}` : ""}`;
  const [debouncedEmbedKey, setDebouncedEmbedKey] = useState(embedKey);
  // Scroll-trap fix, part 2 (part 1 is overflow-anchor:none on .mcs-body):
  // a remount tears the old Stripe iframe out (~600px gone) and the sheet's
  // content collapses under the buyer's finger before the new one grows
  // back — the yo-yo that made scrolling back to the top impossible. Freeze
  // the wrapper at its pre-swap height for the swap, then release a few
  // seconds later so a genuinely shorter form doesn't leave dead padding.
  const embedWrapRef = useRef<HTMLDivElement>(null);
  const [embedMinH, setEmbedMinH] = useState(320);
  useEffect(() => {
    const t = setTimeout(() => {
      const h = embedWrapRef.current?.offsetHeight;
      if (h && h > 320) setEmbedMinH(h);
      setDebouncedEmbedKey(embedKey);
    }, 700);
    return () => clearTimeout(t);
  }, [embedKey]);
  useEffect(() => {
    if (embedMinH <= 320) return;
    const t = setTimeout(() => setEmbedMinH(320), 4000);
    return () => clearTimeout(t);
  }, [debouncedEmbedKey, embedMinH]);

  /** Apply button / Enter key — validates whatever is typed in the input. */
  const applyCoupon = useCallback(() => validateCoupon(couponCode), [couponCode, validateCoupon]);

  /** EXACT same body shape as the desktop rail — do not add or rename keys. */
  const buildCheckoutBody = useCallback(() => {
    const fbp = document.cookie.split("; ").find((c) => c.startsWith("_fbp="))?.split("=")[1];
    const fbc = document.cookie.split("; ").find((c) => c.startsWith("_fbc="))?.split("=")[1];
    return {
      ticketTypeId: activeTicket?.id,
      quantity: qty,
      eventSlug: selected?.effectiveSlug,
      couponCode: couponStatus?.valid ? couponCode : undefined,
      premiumAddon: kidsMode ? false : effectiveVip,
      email: prefillEmail,
      // Kids family mode: campers (first name + age only) + consent flags.
      // The server validates all three and rejects the session without them.
      ...(kidsMode && {
        parentName: parentName.trim(),
        children: campers.map((c) => ({
          firstName: c.firstName.trim(),
          lastName: c.lastName.trim(),
          age: parseInt(c.age, 10),
        })),
        termsAccepted,
        mediaOptIn,
      }),
      ...(fbp && { fbp }),
      ...(fbc && { fbc }),
      ...utmParams,
    };
  }, [activeTicket, qty, selected, couponStatus, couponCode, effectiveVip, prefillEmail, utmParams, kidsMode, campers, parentName, termsAccepted, mediaOptIn]);

  // Bumped by the "Try again" button — included in the embed remount key so a
  // retry remounts the provider, which refetches the client secret.
  const [retryNonce, setRetryNonce] = useState(0);

  // The embedded provider calls this once per mount and shows a silent blank
  // box if it throws (sold out 410, T−1h cutoff, network). Record the
  // server's message into `error` state — rendered aria-live in the embedded
  // branch below with a "Try again" button — before rethrowing so the
  // provider still knows the fetch failed.
  const fetchClientSecret = useCallback(async () => {
    try {
      const res = await fetch("/api/checkout/embedded", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildCheckoutBody()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.clientSecret) {
        throw new Error(data.message || "Checkout failed. Please try again.");
      }
      setError(null);
      return data.clientSecret as string;
    } catch (err) {
      const message =
        err instanceof TypeError // fetch network failure — no server message
          ? "Couldn't reach checkout. Check your connection and try again."
          : err instanceof Error
            ? err.message
            : "Checkout failed. Please try again.";
      setError(message);
      throw err instanceof Error ? err : new Error(message);
    }
  }, [buildCheckoutBody]);

  // Redirect fallback (no publishable key) — POSTs to the hosted Checkout
  // Session and navigates away. Same body, same server builder.
  const startRedirectCheckout = useCallback(async () => {
    setError(null);
    setCheckoutLoading(true);
    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildCheckoutBody()),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Checkout failed");
      if (data.url) window.location.href = data.url;
    } catch (err: any) {
      setError(err?.message || "Something went wrong. Please try again.");
      setCheckoutLoading(false);
    }
  }, [buildCheckoutBody]);

  // "Purchase VIP standalone" — hosted-redirect Stripe checkout containing
  // ONLY the VIP line item (no ticket), for buyers who already hold a seat.
  // Same server-side session builder (coupon, UTM, metadata.eventSlug all
  // reused); `vipOnly: true` drops the ticket line item and stamps
  // metadata.vipStandalone so the webhook skips the seat increment. Always
  // quantity 1 against the individual tier (server validates ticket↔event).
  const startVipOnlyCheckout = useCallback(async () => {
    setError(null);
    setVipOnlyLoading(true);
    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...buildCheckoutBody(),
          ticketTypeId: (indivTicket ?? activeTicket)?.id,
          quantity: 1,
          premiumAddon: true,
          vipOnly: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Checkout failed");
      if (data.url) window.location.href = data.url;
    } catch (err: any) {
      setError(err?.message || "Something went wrong. Please try again.");
      setVipOnlyLoading(false);
    }
  }, [buildCheckoutBody, indivTicket, activeTicket]);

  // `?vip=1` deep link: fire the standalone VIP checkout once, on open.
  const autoVipFired = useRef(false);
  useEffect(() => {
    if (!open || !autoVipOnly || autoVipFired.current || !vipAllowed || !premiumAddon) return;
    autoVipFired.current = true;
    startVipOnlyCheckout();
  }, [open, autoVipOnly, vipAllowed, premiumAddon, startVipOnlyCheckout]);

  if (!open) return null;
  if (typeof document === "undefined") return null; // SSR guard
  if (!selected || !activeTicket) return null;

  const showPicker = !kidsMode && sessions.length > 1;
  const parts = sessionDateParts(selected.session);
  // Kids mode hand-letters the section eyebrows in Crayonize (mirrors the
  // page's .kids-eyebrow, sized down for the sheet's density). Inline style
  // beats the label's font-mono/text-[11px] classes without forking markup.
  const kidsEyebrowStyle = kidsMode
    ? { fontFamily: "var(--font-hand)", fontWeight: 700, fontSize: "16px", letterSpacing: "0.4px" }
    : undefined;
  // Kids camp spans 10 days (4 sessions), so the generic "N-hour workshop"
  // line (computed start→end) would read "218-hour workshop". Override it.
  const formatLine = kidsMode
    ? "Live on Zoom · 4 sessions + Family Demo Night · Ages 10–16"
    : `${event.format === "online" ? "Live on Zoom" : "Live in person"} · ${parts.durationHours}-hour workshop`;

  return createPortal(
    <>
      {/* Desktop drawer backdrop — dims the page, click closes. Mobile is a
          full-screen takeover, so no backdrop below `sm`. */}
      <div
        className="mcs-backdrop hidden sm:block fixed inset-0 z-[119]"
        style={{ backgroundColor: "color-mix(in srgb, var(--charcoal) 55%, transparent)" }}
        onClick={onClose}
        aria-hidden="true"
        data-testid="checkout-drawer-backdrop"
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="Secure checkout"
        tabIndex={-1}
        className="mcs-root fixed top-0 left-0 right-0 z-[120] sm:left-auto sm:w-[560px] sm:max-w-[94vw] flex flex-col bg-cream text-charcoal outline-none"
        data-testid="mobile-checkout-sheet"
      >
      <style>{`
        @keyframes mcs-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes mcs-rise { from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: none } }
        @keyframes mcs-slide { from { transform: translateX(100%) } to { transform: none } }
        /* 100dvh tracks iOS Safari's collapsing toolbars; 100vh is the fallback. */
        /* overflow: clip (not hidden) — the root must NEVER be a scroll
           container. When the Stripe embed iframe (or the sr-only terms
           checkbox) autofocuses on mount, the browser runs scrollIntoView,
           which will scroll an overflow:hidden ancestor — dragging the fixed
           header + close button off the top of the viewport with no way to
           scroll them back (the buyer is stranded, can't close). clip
           refuses all programmatic/focus scrolling; hidden is the fallback
           for browsers without clip. The .mcs-body owns the single scroll. */
        .mcs-root { height: 100vh; height: 100dvh; overflow: hidden; overflow: clip; animation: mcs-fade 200ms cubic-bezier(0.25,0,0.25,1); }
        /* overflow-anchor: none — the Stripe embed iframe at the bottom
           resizes itself repeatedly (mount, camper-edit remounts, 3DS), and
           Chrome's scroll anchoring re-pins the viewport to it on every
           resize, trapping the buyer at the bottom of the sheet (worse with
           each camper added). Anchoring off = the buyer's scroll position
           is theirs again. */
        .mcs-body { min-height: 0; -webkit-overflow-scrolling: touch; animation: mcs-rise 240ms cubic-bezier(0.25,0,0.25,1); overflow-anchor: none; }
        .mcs-backdrop { animation: mcs-fade 240ms cubic-bezier(0.25,0,0.25,1); }
        .mcs-root button, .mcs-root label, .mcs-root a { touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
        /* ≥sm: right-anchored drawer — slide in from the right edge, hard left
           border instead of a shadow (design system: no shadows). */
        @media (min-width: 640px) {
          .mcs-root { animation: mcs-slide 280ms cubic-bezier(0.25,0,0.25,1); border-left: 1px solid color-mix(in srgb, var(--charcoal) 20%, transparent); }
        }
        @media (prefers-reduced-motion: reduce) {
          .mcs-root, .mcs-body, .mcs-backdrop { animation: none; }
        }
      `}</style>

      {/* ── Header — social proof left, close right ── */}
      <div className="shrink-0 flex items-center gap-2 px-5 h-14 border-b border-charcoal/12 bg-cream">
        {/* Kids mode: plain "Secure checkout" (the founding-cohort tagline
            was cut per Isaac 2026-07-21); adult workshops keep the cred line. */}
        <span className="font-mono text-[11px] uppercase tracking-[1.4px] text-charcoal/80 whitespace-nowrap">
          {kidsMode ? "Secure checkout" : "Over 450,000 trained"}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close checkout"
          className="ml-auto w-11 h-11 -mr-2 shrink-0 flex items-center justify-center text-charcoal/50 hover:text-charcoal focus-visible:text-charcoal transition-colors"
          data-testid="mobile-checkout-close"
        >
          <X className="w-5 h-5" aria-hidden="true" />
        </button>
      </div>

      {/* ── Body (single scroll) ── */}
      <div
        // data-lenis-prevent: the site's Lenis smooth-scroll hijacks desktop
        // wheel events for the page; without this the drawer can't scroll.
        data-lenis-prevent
        className="mcs-body flex-1 overflow-y-auto overscroll-contain"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 32px)" }}
      >
        {/* 1. Order */}
        <div className="px-5 pt-5">
          <h2
            className="text-charcoal text-balance"
            // Kids page rule: no serif anywhere — the camp renders its title
            // in the sans stack; every other workshop keeps Copernicus.
            style={
              kidsMode
                ? { fontFamily: "var(--font-sans)", fontSize: 24, lineHeight: 1.15, fontWeight: 700, letterSpacing: "-0.02em" }
                : { fontFamily: "var(--font-display)", fontSize: 25, lineHeight: 1.15, fontWeight: 500 }
            }
          >
            {kidsMode ? <KidsCrayonTitle title={event.title} /> : event.title}
          </h2>
          <p className="text-[13px] text-charcoal/60 mt-1.5">{formatLine}</p>
        </div>

        {/* 2. Date */}
        <fieldset className="px-5 mt-6 border-0 p-0 m-0">
          <legend className="font-mono text-[11px] uppercase tracking-[1.4px] text-charcoal/80 mb-2.5 p-0" style={kidsEyebrowStyle}>
            {kidsMode ? "Camp schedule" : showPicker ? "Choose your date" : "Your date"}
          </legend>
          {kidsMode ? (
            /* One cohort — collapsed to its one-line identity ("August 25 –
               September 3 Cohort"); the chevron twirls the full session
               dates + times down on tap (Isaac 2026-07-21). */
            <div className="bg-white border border-charcoal/15 rounded-[4px]" data-testid="kids-schedule-card">
              <button
                type="button"
                onClick={() => setScheduleOpen((o) => !o)}
                aria-expanded={scheduleOpen}
                className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left"
                data-testid="kids-schedule-toggle"
              >
                <span className="min-w-0">
                  <span className="block text-[15px] font-medium text-charcoal">{KIDS_COHORT_SCHEDULE.cohortLine}</span>
                  <span className="block text-[13px] text-charcoal/60 mt-0.5">{KIDS_COHORT_SCHEDULE.subLine}</span>
                </span>
                <ChevronDown
                  className={`w-4 h-4 shrink-0 text-charcoal/50 transition-transform motion-reduce:transition-none ${scheduleOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>
              {scheduleOpen && (
                /* Six dated touchpoints as simple dotted boxes — 3-up in the
                   drawer, 2-up on narrow mobile (Isaac 2026-07-21 v3: the
                   prose lines read as confusing date soup). */
                <div className="px-4 pb-3.5 pt-3 border-t border-charcoal/10" data-testid="kids-schedule-detail">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                    {KIDS_COHORT_SCHEDULE.items.map((it) => (
                      <div
                        key={it.label}
                        className="border border-dashed border-charcoal/30 rounded-[4px] px-2.5 py-2 min-w-0"
                        data-testid={`kids-schedule-box-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        <span className="block font-mono text-[9px] uppercase tracking-[1px] text-charcoal/50">
                          {it.label}
                          {"note" in it && it.note ? ` · ${it.note}` : ""}
                        </span>
                        <span className="block text-[13px] font-medium text-charcoal mt-0.5 whitespace-nowrap">{it.day}</span>
                        <span className="block text-[11px] text-charcoal/55 mt-0.5 whitespace-nowrap">{it.time}</span>
                      </div>
                    ))}
                  </div>
                  <span className="block text-[12px] text-charcoal/45 mt-2.5">
                    Every session recorded — miss one, keep the material forever.
                  </span>
                </div>
              )}
            </div>
          ) : showPicker ? (
            <DateDropdown
              sessions={sessions}
              selected={selected}
              onSelect={(id) => {
                setSelectedId(id);
                setError(null);
              }}
            />
          ) : (
            <DateOption bookable={selected} selected single onSelect={() => {}} />
          )}

          {/* Urgency — enrollment deadline OR sellout, whichever bites first.
              Carries the selected date's live seat count (the seats-left info
              the old date cards used to show). */}
          {(() => {
            const left = enrollWindowLeft(new Date(selected.session.startDatetime as unknown as string));
            const seats =
              selected.seatsRemaining != null && selected.seatsRemaining > 0 && selected.seatsRemaining < 20
                ? selected.seatsRemaining
                : null;
            if (!left && seats == null) return null;
            return (
              <div
                className="flex items-start gap-2.5 border border-terra/25 rounded-[4px] px-3.5 py-2.5 mt-2"
                style={{ backgroundColor: "rgba(217, 64, 0, 0.06)" }}
                data-testid="mobile-checkout-countdown"
              >
                <Clock className="w-4 h-4 mt-0.5 shrink-0 text-terra" aria-hidden="true" />
                <p className="m-0 text-[13px] leading-snug text-charcoal/75">
                  {left ? (
                    <>
                      <span className="font-medium text-terra">
                        Left to enroll: <span className="tabular-nums">{left}</span>
                      </span>
                      {seats != null ? (
                        kidsMode ? (
                          <> · or until sold out. Only {seats} camper {seats === 1 ? "spot" : "spots"} left.</>
                        ) : (
                          <> · or until sold out. Only {seats} {seats === 1 ? "seat" : "seats"} left for {parts.monthDay}.</>
                        )
                      ) : (
                        <> · or until this {kidsMode ? "cohort" : "date"} sells out.</>
                      )}
                    </>
                  ) : (
                    <span className="font-medium text-terra">
                      {kidsMode
                        ? <>Only {seats} camper {seats === 1 ? "spot" : "spots"} left.</>
                        : <>Only {seats} {seats === 1 ? "seat" : "seats"} left for {parts.monthDay}.</>}
                    </span>
                  )}
                </p>
              </div>
            );
          })()}
        </fieldset>

        {/* 3-KIDS-A. Parent / guardian — captured explicitly (NOT inferred from
            Stripe's cardholder/Link name, which can be a different person). This
            is the authoritative name on the order, confirmation email, and
            calendar invite. */}
        {kidsMode && (
          <div className="px-5 mt-6">
            <p className="font-mono text-[11px] uppercase tracking-[1.4px] text-charcoal/80 mb-2.5" style={kidsEyebrowStyle}>Parent / Guardian</p>
            <input
              type="text"
              value={parentName}
              onChange={(e) => setParentName(e.target.value)}
              placeholder="Parent or guardian's full name"
              autoComplete="name"
              aria-label="Parent or guardian's full name"
              className="w-full h-11 px-3 bg-white border border-charcoal/20 rounded-[4px] text-charcoal text-[16px] placeholder:text-charcoal/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo"
              data-testid="kids-parent-name"
            />
            <p className="text-[12px] text-charcoal/50 mt-1.5 leading-snug">
              Who the roster, calendar invite, and confirmation are addressed to.
            </p>
          </div>
        )}

        {/* 3-KIDS. Campers — family mode replaces the seat stepper with
            per-camper cards (name + age, parent-supplied — that's the entire
            child-data footprint) and the sibling ladder price per card. The
            section starts EMPTY: "Enroll your child +" expands camper 1,
            "Add another child" grows the family (Isaac 2026-07-21). */}
        {kidsMode && (
          <div className="px-5 mt-6">
            <p className="font-mono text-[11px] uppercase tracking-[1.4px] text-charcoal/80 mb-2.5" style={kidsEyebrowStyle}>Campers</p>
            <div className="flex flex-col gap-2">
              {campers.map((c, i) => {
                const pct = KIDS_SIBLING_PERCENTS[i];
                return (
                  <div key={i} className="bg-white border border-charcoal/15 rounded-[4px] px-4 py-3.5" data-testid={`kids-camper-row-${i}`}>
                    <div className="flex items-center justify-between gap-3 mb-2.5">
                      <span className="text-[13px] font-medium text-charcoal">
                        Camper {i + 1}
                        {pct > 0 && <span className="ml-2 font-normal text-forest">{pct}% off</span>}
                      </span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        {/* Full price struck first — the effective price
                            never appears without its anchor (Isaac v3). */}
                        {kidsListLadder[i] > kidsLadder[i] && (
                          <span className="text-[12px] line-through text-charcoal/40 tabular-nums">
                            {formatPrice(kidsListLadder[i])}
                          </span>
                        )}
                        <span className="text-[14px] font-semibold text-charcoal tabular-nums">{formatPrice(kidsLadder[i])}</span>
                        <button
                          type="button"
                          onClick={() => setCampers((cs) => cs.filter((_, j) => j !== i))}
                          aria-label={`Remove camper ${i + 1}`}
                          className="w-11 h-11 -mr-3 -my-1 flex items-center justify-center text-charcoal/40 hover:text-charcoal transition-colors"
                          data-testid={`kids-camper-remove-${i}`}
                        >
                          <X className="w-4 h-4" aria-hidden="true" />
                        </button>
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <input
                        type="text"
                        value={c.firstName}
                        onChange={(e) =>
                          setCampers((cs) => cs.map((row, j) => (j === i ? { ...row, firstName: e.target.value } : row)))
                        }
                        placeholder="First name"
                        autoComplete="off"
                        aria-label={`Camper ${i + 1} first name`}
                        className="flex-1 min-w-[130px] h-11 px-3 bg-white border border-charcoal/20 rounded-[4px] text-charcoal text-[16px] placeholder:text-charcoal/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo"
                        data-testid={`kids-camper-name-${i}`}
                      />
                      <input
                        type="text"
                        value={c.lastName}
                        onChange={(e) =>
                          setCampers((cs) => cs.map((row, j) => (j === i ? { ...row, lastName: e.target.value } : row)))
                        }
                        placeholder="Last name"
                        autoComplete="off"
                        aria-label={`Camper ${i + 1} last name`}
                        className="flex-1 min-w-[130px] h-11 px-3 bg-white border border-charcoal/20 rounded-[4px] text-charcoal text-[16px] placeholder:text-charcoal/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo"
                        data-testid={`kids-camper-lastname-${i}`}
                      />
                      {/* Age is a select, not a birthday picker: less checkout
                          friction, and we never hold a child's birthdate —
                          age alone is the COPPA-lean choice. appearance-none
                          + our own chevron: the native arrow sat flush
                          against the right edge. */}
                      <span className="relative shrink-0">
                        <select
                          value={c.age}
                          onChange={(e) =>
                            setCampers((cs) => cs.map((row, j) => (j === i ? { ...row, age: e.target.value } : row)))
                          }
                          aria-label={`Camper ${i + 1} age`}
                          className={`w-[92px] h-11 pl-3 pr-8 bg-white border border-charcoal/20 rounded-[4px] text-[16px] appearance-none focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo ${
                            c.age ? "text-charcoal" : "text-charcoal/40"
                          }`}
                          data-testid={`kids-camper-age-${i}`}
                        >
                          <option value="" disabled>
                            Age
                          </option>
                          {[10, 11, 12, 13, 14, 15, 16].map((a) => (
                            <option key={a} value={String(a)}>
                              {a}
                            </option>
                          ))}
                        </select>
                        <ChevronDown
                          className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 text-charcoal/50 pointer-events-none"
                          aria-hidden="true"
                        />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {campers.length === 0 ? (
              <button
                type="button"
                onClick={() => setCampers([{ firstName: "", lastName: "", age: "" }])}
                className="w-full h-12 border border-dashed border-charcoal/30 rounded-[4px] text-[14px] font-medium text-charcoal/80 hover:text-charcoal hover:border-charcoal/60 transition-colors flex items-center justify-center gap-1.5"
                data-testid="kids-enroll-child"
              >
                <Plus className="w-4 h-4" aria-hidden="true" />
                Enroll your child — {formatPrice(kidsBaseCents)}
                {kidsListBaseCents > kidsBaseCents && (
                  <span className="line-through text-charcoal/40 font-normal">{formatPrice(kidsListBaseCents)}</span>
                )}
              </button>
            ) : (
              campers.length < KIDS_MAX_CHILDREN && (
                <button
                  type="button"
                  onClick={() => setCampers((cs) => [...cs, { firstName: "", lastName: "", age: "" }])}
                  className="mt-2 w-full h-11 border border-dashed border-charcoal/30 rounded-[4px] text-[13px] text-charcoal/70 hover:text-charcoal hover:border-charcoal/60 transition-colors flex items-center justify-center gap-1.5"
                  data-testid="kids-add-camper"
                >
                  <Plus className="w-4 h-4" aria-hidden="true" />
                  Add another child — {KIDS_SIBLING_PERCENTS[campers.length]}% off
                </button>
              )
            )}
            {/* One laptop is fine; own screens are better. Surfaces only
                once a second camper exists. */}
            {campers.length > 1 && (
              <div
                className="flex items-start gap-2.5 border border-indigo/25 rounded-[4px] px-3.5 py-2.5 mt-2"
                style={{ backgroundColor: "hsla(246, 42%, 40%, 0.06)" }}
                data-testid="kids-laptop-callout"
              >
                <Laptop className="w-4 h-4 mt-0.5 shrink-0 text-indigo" aria-hidden="true" />
                <p className="m-0 text-[13px] leading-snug text-charcoal/75">
                  Siblings can share one laptop — but to get the most out of the training, it&rsquo;s best for each
                  camper to have their own computer or iPad.
                </p>
              </div>
            )}
            <p className="text-[12px] text-charcoal/55 mt-2 leading-snug">
              Name and age only — it&rsquo;s how we set up the roster, Demo Night, and each camper&rsquo;s diploma.
              Younger than 10?{" "}
              <a href="/contact" className="underline underline-offset-2">
                Email us
              </a>{" "}
              — waitlist for a junior cohort.
            </p>
          </div>
        )}

        {/* 3. Seats — the tier is derived from the count (1–2 = individual,
            3+ = team rate, 6+ = volume rate), so there is no separate ticket
            picker. The per-seat price flips live as the count crosses tiers. */}
        {!kidsMode && (
        <div className="px-5 mt-6">
          <p className="font-mono text-[11px] uppercase tracking-[1.4px] text-charcoal/80 mb-2.5">Seats</p>
          <div className="bg-white border border-charcoal/15 rounded-[4px] px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <span className="min-w-0">
                <span id="mcs-qty-label" className="block text-[15px] font-medium text-charcoal">
                  {qty} {qty === 1 ? "seat" : "seats"}
                </span>
                <span className="block text-[13px] text-charcoal/60 mt-0.5" aria-live="polite">
                  <span className="tabular-nums">{formatPrice(basePerSeat)}</span> per seat
                </span>
                {teamActive && indivTicket && indivTicket.priceCents > basePerSeat && (
                  <span className="block text-[12px] text-forest mt-0.5 whitespace-nowrap">
                    {Math.round((1 - basePerSeat / indivTicket.priceCents) * 100)}% off with{" "}
                    {volumeActive ? "volume" : "team"} rate
                  </span>
                )}
              </span>
              <div className="flex items-center gap-1 shrink-0" role="group" aria-labelledby="mcs-qty-label">
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  disabled={qty <= 1}
                  aria-label="Decrease seats"
                  className="w-10 h-10 flex items-center justify-center rounded-[4px] text-charcoal/60 hover:text-charcoal disabled:opacity-30 transition-colors"
                  data-testid="mobile-checkout-qty-minus"
                >
                  <Minus className="w-4 h-4" aria-hidden="true" />
                </button>
                <span
                  className="w-8 text-center text-[16px] font-medium text-charcoal tabular-nums"
                  data-testid="mobile-checkout-qty"
                >
                  {qty}
                </span>
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.min(maxSeats, q + 1))}
                  disabled={qty >= maxSeats}
                  aria-label="Increase seats"
                  className="w-10 h-10 flex items-center justify-center rounded-[4px] text-charcoal/60 hover:text-charcoal disabled:opacity-30 transition-colors"
                  data-testid="mobile-checkout-qty-plus"
                >
                  <Plus className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
          {/* Team-rate incentive — only while below the threshold */}
          {!teamActive && teamTicket && indivTicket && teamTicket.priceCents < indivTicket.priceCents && (
            <div
              className="flex items-center gap-2.5 border border-forest/25 rounded-[4px] px-3.5 py-2.5 mt-2"
              style={{ backgroundColor: "hsla(160, 35%, 32%, 0.07)" }}
              data-testid="mobile-checkout-team-hint"
            >
              <Users className="w-4 h-4 shrink-0 text-forest" aria-hidden="true" />
              <p className="m-0 text-[13px] leading-snug text-charcoal/75">
                <span className="font-medium text-forest">Team rate:</span> 3+ seats drop to{" "}
                {formatPrice(teamTicket.priceCents)}/seat. You save{" "}
                {formatPrice(indivTicket.priceCents - teamTicket.priceCents)} per seat.
              </p>
            </div>
          )}

          {/* VIP addon */}
          {vipAllowed && premiumAddon && (
            <label className="flex items-start gap-3 bg-white border border-charcoal/15 rounded-[4px] px-4 py-3.5 mt-2 cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-indigo">
              <input
                type="checkbox"
                checked={effectiveVip}
                onChange={(e) => setVip(e.target.checked)}
                className="sr-only"
                data-testid="mobile-checkout-vip"
              />
              <span
                aria-hidden="true"
                className={`w-5 h-5 mt-0.5 shrink-0 rounded-[3px] border flex items-center justify-center transition-colors ${
                  effectiveVip ? "bg-charcoal border-charcoal" : "border-charcoal/30 bg-white"
                }`}
              >
                {effectiveVip && <Check className="w-3.5 h-3.5 text-cream" strokeWidth={3} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] text-charcoal font-medium">{premiumAddon.label}</span>
                <span className="block text-[12px] text-charcoal/55 mt-0.5 leading-snug">{premiumAddon.description}</span>
              </span>
              <span className="shrink-0 text-[14px] font-semibold text-charcoal tabular-nums">
                +{formatPrice(premiumAddon.priceCents)}
                {ticketKind === "team" && <span className="font-normal text-charcoal/50">/seat</span>}
              </span>
            </label>
          )}

          {/* VIP standalone — for buyers who already hold a seat and only
              want the upgrade. Hosted-redirect checkout, VIP line item only. */}
          {vipAllowed && premiumAddon && (
            <button
              type="button"
              disabled={vipOnlyLoading}
              onClick={startVipOnlyCheckout}
              className="mt-2 w-full h-10 border border-charcoal/25 rounded-[4px] text-charcoal/70 hover:text-charcoal hover:border-charcoal/50 text-[11px] font-mono uppercase tracking-[0.8px] transition-colors disabled:opacity-50 disabled:cursor-wait"
              data-testid="mobile-checkout-vip-standalone"
            >
              {vipOnlyLoading ? "Opening checkout…" : "Purchase VIP standalone"}
            </button>
          )}
        </div>
        )}

        {/* 4. Order summary */}
        <div className="px-5 mt-6">
          <p className="font-mono text-[11px] uppercase tracking-[1.4px] text-charcoal/80 mb-2.5" style={kidsEyebrowStyle}>Order summary</p>
          <div className="bg-white border border-charcoal/15 rounded-[4px] px-4 py-4">
            {/* Kids mode: one line per camper (by name once typed) at FULL
                list price + Family savings and Early bird discount lines
                that resolve to the subtotal. Promo UI honors scoped codes. */}
            {kidsMode && (
              <>
                {campers.length === 0 && (
                  <div className="text-[14px] text-charcoal/45" data-testid="kids-summary-empty">
                    No campers enrolled yet — tap &ldquo;Enroll your child&rdquo; above.
                  </div>
                )}
                {/* Every camper at full list price — the two savings lines
                    below subtract down to the subtotal, so the math a parent
                    adds in their head actually resolves. */}
                {campers.map((c, i) => (
                  <div
                    key={i}
                    className={`flex items-baseline justify-between text-[14px] text-charcoal/75 ${i > 0 ? "mt-2" : ""}`}
                  >
                    <span className="min-w-0 truncate">{c.firstName.trim() || `Camper ${i + 1}`}</span>
                    <span className="tabular-nums shrink-0">{formatPrice(kidsListBaseCents)}</span>
                  </div>
                ))}
                {kidsFamilySavings > 0 && (
                  <div className="flex items-baseline justify-between text-[14px] text-forest mt-2" data-testid="kids-family-savings">
                    <span>Family savings</span>
                    <span className="tabular-nums">−{formatPrice(kidsFamilySavings)}</span>
                  </div>
                )}
                {kidsEarlyBirdSavings > 0 && (
                  <div className="flex items-baseline justify-between text-[14px] text-forest mt-2" data-testid="kids-earlybird-savings">
                    <span>Early bird discount</span>
                    <span className="tabular-nums">−{formatPrice(kidsEarlyBirdSavings)}</span>
                  </div>
                )}
              </>
            )}
            {/* When a discount line renders below, the top line anchors at the
                pre-discount rate so the visible math resolves to the Total. */}
            {!kidsMode && (
            <div className="flex items-baseline justify-between text-[14px] text-charcoal/75">
              <span>
                {formatPrice(savings > 0 ? perSeatAnchor : basePerSeat)} × {qty} {qty === 1 ? "seat" : "seats"}
              </span>
              <span className="tabular-nums">{formatPrice((savings > 0 ? perSeatAnchor : basePerSeat) * qty)}</span>
            </div>
            )}
            {effectiveVip && premiumAddon && (
              <div className="flex items-baseline justify-between text-[14px] text-charcoal/75 mt-2">
                <span>{premiumAddon.label}</span>
                <span className="tabular-nums">+{formatPrice(premiumAddon.priceCents * qty)}</span>
              </div>
            )}
            {!kidsMode && savings > 0 && (
              <div className="flex items-baseline justify-between text-[14px] text-forest mt-2">
                <span>{teamActive ? (volumeActive ? "Volume discount" : "Team discount") : "Early-bird savings"}</span>
                <span className="tabular-nums">−{formatPrice(savings)}</span>
              </div>
            )}
            {couponStatus?.valid && couponDiscount > 0 && (
              <div className="flex items-baseline justify-between text-[14px] text-forest mt-2">
                <span className="flex items-center gap-2">
                  {couponStatus.label || couponCode.toUpperCase()}
                  <button
                    type="button"
                    onClick={() => {
                      setCouponStatus(null);
                      setCouponCode("");
                    }}
                    aria-label="Remove promo code"
                    className="text-charcoal/40 hover:text-charcoal transition-colors"
                  >
                    <X className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                </span>
                <span className="tabular-nums">−{formatPrice(couponDiscount)}</span>
              </div>
            )}

            {/* Promo code — collapsed by default, Maven-style. Kids mode
                honors kids-SCOPED codes only (5TJT/JEWISHLINK etc.); global
                codes validate invalid for this series. */}
            {!couponStatus?.valid && (
              <div className="mt-3">
                {!couponOpen ? (
                  <button
                    type="button"
                    onClick={() => setCouponOpen(true)}
                    className="flex items-center gap-1.5 text-[13px] text-indigo underline underline-offset-4 decoration-indigo/30 hover:decoration-indigo transition-colors"
                    data-testid="mobile-checkout-coupon-toggle"
                  >
                    <Plus className="w-3.5 h-3.5" aria-hidden="true" />
                    Add promo code
                  </button>
                ) : (
                  /* Compact on purpose — the promo row is a utility, not a
                     hero. 16px input font stays (iOS zoom guard); height and
                     the Apply button shrink. */
                  <div className="flex gap-1.5 max-w-[260px]">
                    <input
                      type="text"
                      name="coupon"
                      aria-label="Promo code"
                      autoComplete="off"
                      spellCheck={false}
                      autoCapitalize="characters"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value);
                        setCouponStatus(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") applyCoupon();
                      }}
                      placeholder="Promo code"
                      className="flex-1 min-w-0 h-9 px-2.5 bg-white border border-charcoal/20 rounded-[4px] text-charcoal text-[16px] font-mono uppercase placeholder:text-charcoal/30 placeholder:normal-case placeholder:text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo"
                      data-testid="mobile-checkout-coupon-input"
                    />
                    <button
                      type="button"
                      disabled={!couponCode.trim() || couponLoading}
                      onClick={applyCoupon}
                      className="h-9 px-2.5 border border-charcoal/25 rounded-[4px] text-charcoal/70 hover:text-charcoal hover:border-charcoal/50 text-[11px] font-mono uppercase transition-colors disabled:opacity-40"
                      data-testid="mobile-checkout-coupon-apply"
                    >
                      {couponLoading ? "…" : "Apply"}
                    </button>
                  </div>
                )}
              </div>
            )}
            <p aria-live="polite" className="m-0">
              {couponStatus && !couponStatus.valid && (
                <span className="block text-[12px] mt-1.5 font-mono text-terra" data-testid="mobile-checkout-coupon-status">
                  {couponStatus.message || "That code isn't valid. Check it and try again."}
                </span>
              )}
            </p>

            <div className="flex items-baseline justify-between border-t border-charcoal/12 mt-4 pt-3.5">
              <span className="text-[15px] font-medium text-charcoal">Total</span>
              <span className="flex items-baseline gap-2">
                {/* Crossed-out pre-coupon total — the promo's math sits right
                    next to the final price, unmissable. */}
                {couponDiscount > 0 && (
                  <span className="text-[14px] text-charcoal/40 line-through tabular-nums" data-testid="mobile-checkout-total-strike">
                    {formatPrice(subtotal)}
                  </span>
                )}
                <span className="text-[20px] font-semibold text-charcoal tabular-nums" data-testid="mobile-checkout-total">
                  {formatPrice(total)}
                </span>
              </span>
            </div>
            {/* BNPL framing (kids: Isaac 2026-07-22) — Klarna is live on the
                account; the quarter-total makes the price feel decidable.
                "Available" keeps it honest: Klarna decides eligibility per
                shopper at payment time. */}
            {kidsMode && total > 0 && (
              <p className="text-[12px] text-charcoal/55 text-right mt-1.5 mb-0" data-testid="kids-klarna-line">
                or 4 interest-free payments of{" "}
                <span className="font-medium text-charcoal/75 tabular-nums">{formatPrice(Math.round(total / 4))}</span>{" "}
                with Klarna, available at payment
              </p>
            )}
          </div>
        </div>

        {/* 5. Guarantee — kids camp: Money-Back Satisfaction Guarantee
            (renamed from "Session-One Guarantee", Isaac 2026-07-21). Window
            MUST match Program Terms §5: full refund from purchase until one
            hour after session one ends; final after that. */}
        <div className="px-5 mt-4">
          <div className="flex items-start gap-3 bg-white border border-charcoal/15 rounded-[4px] px-4 py-3.5">
            {kidsMode ? (
              <ThumbsUp className="w-[18px] h-[18px] mt-0.5 shrink-0 text-forest" aria-hidden="true" />
            ) : (
              <Shield className="w-[18px] h-[18px] mt-0.5 shrink-0 text-forest" aria-hidden="true" />
            )}
            <div>
              <p className="text-[13px] font-medium text-charcoal">
                {kidsMode ? "Money-Back Satisfaction Guarantee" : "100% money-back guarantee"}
              </p>
              <p className="text-[12px] text-charcoal/55 mt-0.5 leading-snug">
                {kidsMode
                  ? "Join the first session. If camp isn't the right fit, tell us within one hour after it ends for a full, 100% refund — no reason required."
                  : "Not satisfied? Email us within 24 hours after the workshop and we'll refund you in full."}
              </p>
            </div>
          </div>
        </div>

        {/* 5-KIDS. Consents — one required Program Terms checkbox + one
            separate, deliberately UNCHECKED media opt-in (projects only,
            never the child's face). Payment stays locked until the required
            box is checked. */}
        {kidsMode && kids && (
          <div className="px-5 mt-4">
            <div className="bg-white border border-charcoal/15 rounded-[4px] px-4 py-3.5 flex flex-col gap-4">
              <label className="flex items-start gap-3 cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-indigo rounded-[3px]">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="sr-only"
                  data-testid="kids-terms-checkbox"
                />
                <span
                  aria-hidden="true"
                  className={`w-5 h-5 mt-0.5 shrink-0 rounded-[3px] border flex items-center justify-center transition-colors ${
                    termsAccepted ? "bg-charcoal border-charcoal" : "border-charcoal/30 bg-white"
                  }`}
                >
                  {termsAccepted && <Check className="w-3.5 h-3.5 text-cream" strokeWidth={3} />}
                </span>
                <span className="text-[13px] text-charcoal/75 leading-snug">
                  I am the parent or legal guardian of the camper(s), I am at least 18, and I agree to the{" "}
                  {/* Opens the scrollable in-sheet modal (clickwrap: conspicuous
                      notice + this affirmative checkbox; scroll-to-bottom is
                      deliberately NOT required — counsel rec 2026-07-21). */}
                  <button
                    type="button"
                    onClick={() => setTermsModalOpen(true)}
                    className="underline underline-offset-2 text-indigo font-medium"
                    data-testid="kids-terms-link"
                  >
                    Program Terms &amp; Waiver
                  </button>
                  , including the refund policy and the AI acknowledgment.{" "}
                  <span className="text-charcoal/50">Required.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-indigo rounded-[3px]">
                <input
                  type="checkbox"
                  checked={mediaOptIn}
                  onChange={(e) => setMediaOptIn(e.target.checked)}
                  className="sr-only"
                  data-testid="kids-media-optin"
                />
                <span
                  aria-hidden="true"
                  className={`w-5 h-5 mt-0.5 shrink-0 rounded-[3px] border flex items-center justify-center transition-colors ${
                    mediaOptIn ? "bg-charcoal border-charcoal" : "border-charcoal/30 bg-white"
                  }`}
                >
                  {mediaOptIn && <Check className="w-3.5 h-3.5 text-cream" strokeWidth={3} />}
                </span>
                <span className="text-[13px] text-charcoal/75 leading-snug">
                  You may share what my child builds — project screenshots with first name and last initial only,
                  never photos or video of my child. <span className="text-charcoal/50">Optional.</span>
                </span>
              </label>
            </div>
          </div>
        )}

        {/* 7. Payment — kids mode keeps this locked until every camper row
            is complete and the Program Terms box is checked. That also
            guarantees metadata.children is final when the embed mounts (the
            embed key remounts it, debounced, on any later edit). */}
        <div className="px-5 mt-6">
          <p className="font-mono text-[11px] uppercase tracking-[1.4px] text-charcoal/80 mb-2.5" style={kidsEyebrowStyle}>Payment</p>

          {!kidsPaymentUnlocked ? (
            <div
              className="bg-white border border-dashed border-charcoal/25 rounded-[4px] px-4 py-6 text-center"
              data-testid="kids-payment-locked"
            >
              <Lock className="w-4 h-4 mx-auto mb-2 text-charcoal/35" aria-hidden="true" />
              <p className="text-[13px] text-charcoal/55 m-0 leading-snug">
                {campers.length === 0
                  ? "Tap “Enroll your child” above to get started."
                  : !parentNameValid
                    ? "Add the parent or guardian's name above to continue to payment."
                    : !campersComplete
                      ? "Add each camper's name and age to continue to payment."
                      : "Accept the Program Terms above to continue to payment."}
              </p>
            </div>
          ) : useEmbedded ? (
            /* Stripe owns the fields, wallets, email, and pay button in here. */
            <div
              ref={embedWrapRef}
              className="bg-white border border-charcoal/15 rounded-[4px] p-1"
              style={{ minHeight: embedMinH }}
              data-testid="mobile-checkout-embedded"
            >
              {/* fetchClientSecret failures land here — the sold-out /
                  cutoff / network message with a retry, never a silent
                  blank box. The aria-live wrapper stays mounted so the
                  announcement fires when the error appears. */}
              <p aria-live="assertive" className="m-0">
                {error && (
                  <span
                    className="block text-[12px] text-terra font-mono text-center leading-snug px-4 pt-5"
                    data-testid="mobile-checkout-error"
                  >
                    {error}
                  </span>
                )}
              </p>
              {error ? (
                <div className="flex justify-center px-4 pb-6 pt-3">
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setRetryNonce((n) => n + 1);
                    }}
                    className="h-11 px-5 border border-charcoal/25 rounded-[4px] text-charcoal/70 hover:text-charcoal hover:border-charcoal/50 text-[12px] font-mono uppercase transition-colors"
                    data-testid="mobile-checkout-retry"
                  >
                    Try again
                  </button>
                </div>
              ) : (
                stripePromiseForRender && (
                  <EmbeddedCheckoutProvider
                    key={`${debouncedEmbedKey}#${retryNonce}`}
                    stripe={stripePromiseForRender}
                    options={{ fetchClientSecret }}
                  >
                    <EmbeddedCheckout />
                  </EmbeddedCheckoutProvider>
                )
              )}
            </div>
          ) : (
            <>
              <p aria-live="assertive" className="m-0">
                {error && (
                  <span className="block text-[12px] text-terra font-mono mb-2" data-testid="mobile-checkout-error">
                    {error}
                  </span>
                )}
              </p>
              <button
                type="button"
                disabled={checkoutLoading}
                onClick={startRedirectCheckout}
                className="w-full h-[54px] rounded-[4px] font-mono text-[15px] tracking-[0.8px] uppercase disabled:opacity-70 disabled:cursor-wait flex items-center justify-center gap-2 transition-[filter] hover:brightness-90 focus-visible:ring-2 focus-visible:ring-indigo"
                style={{ backgroundColor: "var(--terra)", color: "var(--cream)" }}
                data-testid="mobile-checkout-pay"
              >
                {checkoutLoading ? (
                  "Processing…"
                ) : (
                  <>
                    <Lock className="w-4 h-4" aria-hidden="true" />
                    Pay {formatPrice(total)}
                  </>
                )}
              </button>
            </>
          )}

          {/* Terms — standard checkout convention, right where consent happens */}
          <p className="text-[11px] text-charcoal/50 text-center leading-snug mt-3.5">
            By completing your purchase you agree to {kidsMode && kids ? (
              <>
                the{" "}
                <a href={kids.termsHref} className="underline underline-offset-2 hover:text-charcoal transition-colors">
                  Program Terms
                </a>
                , our{" "}
              </>
            ) : (
              <>our{" "}</>
            )}
            <a href="/terms" className="underline underline-offset-2 hover:text-charcoal transition-colors">
              Terms
            </a>{" "}
            and{" "}
            <a href="/privacy" className="underline underline-offset-2 hover:text-charcoal transition-colors">
              Privacy Policy
            </a>
            .
          </p>

          {/* Testimonial — social proof, the closing note of the checkout.
              Kids mode drops the corporate T-Mobile quote + logo strip and
              keeps ONE real review, clearly labeled as coming from the adult
              workshops (FTC 465: no implying kids-program reviews exist). */}
          {!kidsMode && (
          <figure className="bg-white border border-charcoal/15 rounded-[4px] px-4 py-4 m-0 mt-4" data-testid="mobile-checkout-testimonial">
            <div className="flex items-center gap-0.5 mb-2" aria-label="Rated 5 out of 5 stars" role="img">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-3.5 h-3.5 text-terra" fill="currentColor" aria-hidden="true" />
              ))}
            </div>
            <blockquote className="m-0 text-[14px] leading-relaxed text-charcoal/80">
              &ldquo;{CHECKOUT_TESTIMONIAL.text}&rdquo;
            </blockquote>
            <figcaption className="mt-3 flex items-center justify-between gap-3">
              <span className="text-[12px] text-charcoal/55">
                <span className="font-medium text-charcoal/75">{CHECKOUT_TESTIMONIAL.name}</span>
              </span>
              <img
                src={CHECKOUT_TESTIMONIAL.logo}
                alt={CHECKOUT_TESTIMONIAL.logoAlt}
                width={88}
                height={16}
                loading="lazy"
                className="h-4 w-auto object-contain shrink-0"
                style={{ filter: "grayscale(1)", opacity: 0.55 }}
              />
            </figcaption>
          </figure>
          )}

          {/* Second voice — kids mode closes on REAL camp voices (one parent,
              one camper — supplied verbatim by Isaac 2026-07-21); the adult
              workshops keep Isaac's lead Google review. */}
          {kidsMode ? (
            KIDS_CHECKOUT_TESTIMONIALS.map((t, idx) => (
              <figure
                key={t.name}
                className="bg-white border border-charcoal/15 rounded-[4px] px-4 py-4 m-0 mt-3"
                data-testid={`kids-checkout-testimonial-${idx}`}
              >
                <div className="flex items-center gap-0.5 mb-2" aria-label="Rated 5 out of 5 stars" role="img">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="w-3.5 h-3.5 text-terra" fill="currentColor" aria-hidden="true" />
                  ))}
                </div>
                <blockquote className="m-0 text-[14px] leading-relaxed text-charcoal/80">
                  &ldquo;{t.text}&rdquo;
                </blockquote>
                <figcaption className="mt-3 text-[12px] text-charcoal/55">
                  <span className="font-medium text-charcoal/75">{t.name}</span>
                  <span> · {t.source}</span>
                </figcaption>
              </figure>
            ))
          ) : checkoutReview ? (
            <figure className="bg-white border border-charcoal/15 rounded-[4px] px-4 py-4 m-0 mt-3" data-testid="checkout-testimonial-2">
              <div className="flex items-center gap-0.5 mb-2" aria-label="Rated 5 out of 5 stars" role="img">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-3.5 h-3.5 text-terra" fill="currentColor" aria-hidden="true" />
                ))}
              </div>
              <blockquote className="m-0 text-[14px] leading-relaxed text-charcoal/80">
                &ldquo;{checkoutReview.text}&rdquo;
              </blockquote>
              <figcaption className="mt-3 text-[12px] text-charcoal/55">
                <span className="font-medium text-charcoal/75">{checkoutReview.name}</span>
                <span> · {checkoutReview.source}</span>
              </figcaption>
            </figure>
          ) : null}

          {/* Client logo strip — quiet institutional proof to close on. */}
          {!kidsMode && (
          <div className="mt-5" data-testid="checkout-logo-strip">
            <p className="font-mono text-[10px] uppercase tracking-[1.4px] text-charcoal/45 text-center mb-3">
              Teams trained from
            </p>
            <div className="grid grid-cols-3 gap-x-4 gap-y-7 items-center justify-items-center">
              {CHECKOUT_LOGOS.map((l) => (
                <img
                  key={l.src}
                  src={l.src}
                  alt={l.alt}
                  loading="lazy"
                  className="h-[20px] max-w-[110px] w-auto object-contain"
                  style={{ filter: "grayscale(1)", opacity: 0.5 }}
                />
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
      </div>

      {/* ── Program Terms & Waiver — scrollable modal (kids mode) ──
          Clickwrap surface for the required checkbox above. Same document as
          the canonical /program-terms page (one source, two surfaces). Sits
          inside the sheet's portal tree so the focus trap includes it. */}
      {kidsMode && kids && termsModalOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-0 sm:p-6" data-testid="kids-terms-modal">
          <div
            className="absolute inset-0"
            style={{ backgroundColor: "color-mix(in srgb, var(--charcoal) 55%, transparent)" }}
            onClick={() => setTermsModalOpen(false)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Program Terms and Waiver"
            className="relative bg-cream w-full h-full sm:h-auto sm:max-h-[86dvh] sm:max-w-[680px] sm:rounded-[4px] sm:border sm:border-charcoal/20 flex flex-col overflow-hidden"
          >
            <div className="shrink-0 flex items-center gap-2 px-5 h-14 border-b border-charcoal/12">
              <span className="font-mono text-[11px] uppercase tracking-[1.4px] text-charcoal/80">
                Program Terms &amp; Waiver
              </span>
              <a
                href={kids.termsHref}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto text-[12px] text-indigo underline underline-offset-2 whitespace-nowrap"
                data-testid="kids-terms-full-page"
              >
                View full page ↗
              </a>
              <button
                type="button"
                onClick={() => setTermsModalOpen(false)}
                aria-label="Close Program Terms"
                autoFocus
                className="w-11 h-11 -mr-2 shrink-0 flex items-center justify-center text-charcoal/50 hover:text-charcoal transition-colors"
                data-testid="kids-terms-modal-close"
              >
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
            <div data-lenis-prevent className="flex-1 overflow-y-auto overscroll-contain px-5 py-5">
              <KidsProgramTermsBody />
            </div>
            <div className="shrink-0 border-t border-charcoal/12 px-5 py-3 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setTermsAccepted(true);
                  setTermsModalOpen(false);
                }}
                className="h-11 px-5 rounded-[4px] font-mono text-[12px] tracking-[0.8px] uppercase transition-[filter] hover:brightness-90"
                style={{ backgroundColor: "var(--charcoal)", color: "var(--cream)" }}
                data-testid="kids-terms-agree"
              >
                Agree and continue
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}

/** Kids palette hues for the "Kids" word, hardcoded because the sheet
 *  portals into document.body — OUTSIDE .kids-scope, so the page's
 *  --kids-* custom properties don't reach it. Values mirror index.css. */
const KIDS_CRAYON_COLORS = [
  "hsl(224, 68%, 46%)", // cobalt
  "var(--terra)",
  "hsl(41, 96%, 48%)", // marigold
  "hsl(145, 52%, 36%)", // leaf
];

/** Brighter variants for dark surfaces (the charcoal sticky bar) — the
 *  cream-tuned hues above go muddy on near-black. */
export const KIDS_CRAYON_COLORS_ON_DARK = [
  "hsl(224, 85%, 68%)", // cobalt, lifted
  "#E8643A", // terra, lifted
  "hsl(41, 96%, 55%)", // marigold
  "hsl(145, 55%, 55%)", // leaf, lifted
];

/** Title with the word "Kids" hand-lettered in multicolor Crayonize —
 *  mirrors the page hero's CrayonWord treatment (Isaac 2026-07-21). Used by
 *  the sheet header AND the sticky reserve bar (pass the on-dark colors
 *  there). */
export function KidsCrayonTitle({ title, colors = KIDS_CRAYON_COLORS }: { title: string; colors?: readonly string[] }) {
  return (
    <>
      {title.split(/(kids)/i).map((part, i) =>
        /^kids$/i.test(part) ? (
          <span
            key={i}
            className="whitespace-nowrap"
            style={{ fontFamily: "var(--font-hand)", fontWeight: 700, fontSize: "1.12em" }}
          >
            {part.split("").map((ch, j) => (
              <span
                key={j}
                style={{
                  color: colors[j % colors.length],
                  display: "inline-block",
                  transform: `rotate(${[-5, 4, -3, 5][j % 4]}deg)`,
                }}
              >
                {ch}
              </span>
            ))}
          </span>
        ) : (
          part
        ),
      )}
    </>
  );
}

/**
 * One selectable date — a real radio input under a white card so keyboard
 * and screen-reader semantics come for free. Selected = 2px charcoal border
 * + filled radio dot (traditional, high-contrast).
 */
function DateOption({
  bookable,
  selected,
  single,
  onSelect,
}: {
  bookable: BookableSession;
  selected: boolean;
  single: boolean;
  onSelect: () => void;
}) {
  const { session, seatsRemaining } = bookable;
  const parts = sessionDateParts(session);
  const tight = seatsRemaining != null && seatsRemaining > 0 && seatsRemaining < 20;

  return (
    <label
      className={`flex items-center gap-3.5 bg-white rounded-[4px] px-4 py-3.5 cursor-pointer transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-indigo ${
        selected ? "border-2 border-charcoal" : "border border-charcoal/15 hover:border-charcoal/40"
      }`}
      style={selected ? undefined : { padding: "15px 17px" }} /* keep 1px vs 2px border from shifting layout */
      data-testid={`mobile-checkout-date-${bookable.id}`}
    >
      <input
        type="radio"
        name="mcs-date"
        checked={selected}
        onChange={onSelect}
        disabled={single}
        className="sr-only"
        aria-label={`${parts.weekday}, ${parts.monthDay}, ${parts.timeRange}`}
      />
      {!single && (
        <span
          aria-hidden="true"
          className={`w-5 h-5 shrink-0 rounded-full border flex items-center justify-center ${
            selected ? "border-charcoal" : "border-charcoal/30"
          }`}
        >
          {selected && <span className="w-2.5 h-2.5 rounded-full bg-charcoal" />}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-medium text-charcoal">
          {parts.weekday}, {parts.monthDay}
        </span>
        <span className="block text-[13px] text-charcoal/60 mt-0.5">{parts.timeRange}</span>
      </span>
      {tight && (
        <span className="shrink-0 text-[10px] font-mono uppercase tracking-wide px-2 py-1 rounded-full bg-terra/10 text-terra">
          {seatsRemaining} left
        </span>
      )}
    </label>
  );
}

/**
 * Designed date dropdown — a listbox styled to match the sheet's cards
 * (white, hairline borders, no shadows), instead of the bare native select.
 * Options show weekday + date + time + seats-left; selected gets a check.
 */
function DateDropdown({
  sessions,
  selected,
  onSelect,
}: {
  sessions: BookableSession[];
  selected: BookableSession;
  onSelect: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    // Capture phase so Escape closes the dropdown WITHOUT also closing the
    // sheet (whose own Escape listener runs later, in the bubble phase).
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Workshop date"
        onClick={() => setOpen((o) => !o)}
        className="w-full h-[52px] bg-white border border-charcoal/15 rounded-[4px] px-4 flex items-center justify-between gap-3 text-left hover:border-charcoal/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo transition-colors"
        data-testid="mobile-checkout-date-select"
      >
        <span className="min-w-0 truncate text-[15px] font-medium text-charcoal">
          {sessionDateShort(selected.session)}
        </span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 text-charcoal/50 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label="Workshop date"
          className="absolute left-0 right-0 z-20 mt-1.5 m-0 p-0 list-none bg-white border border-charcoal/25 rounded-[4px] max-h-[45dvh] overflow-y-auto divide-y divide-charcoal/10"
          data-testid="mobile-checkout-date-options"
        >
          {sessions.map((s) => {
            const isSel = s.id === selected.id;
            const p = sessionDateParts(s.session);
            const tight = s.seatsRemaining != null && s.seatsRemaining > 0 && s.seatsRemaining < 20;
            return (
              <li key={s.id} role="option" aria-selected={isSel} className="m-0 p-0">
                <button
                  type="button"
                  onClick={() => {
                    onSelect(s.id);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${
                    isSel ? "bg-cream" : "hover:bg-cream/60"
                  }`}
                  data-testid={`mobile-checkout-date-opt-${s.id}`}
                >
                  <span className="min-w-0">
                    <span className="block text-[14px] font-medium text-charcoal">
                      {p.weekday}, {p.monthDay}
                    </span>
                    <span className="block text-[12px] text-charcoal/55 mt-0.5">{p.timeRange}</span>
                  </span>
                  <span className="shrink-0 flex items-center gap-2">
                    {tight && (
                      <span className="text-[10px] font-mono uppercase tracking-wide px-2 py-1 rounded-full bg-terra/10 text-terra">
                        {s.seatsRemaining} left
                      </span>
                    )}
                    {isSel && <Check className="w-4 h-4 text-charcoal" strokeWidth={3} aria-hidden="true" />}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

